<template>
  <div class="bg-gray-50 min-h-screen">
    <section class="py-12 md:py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Header -->
        <div class="text-center mb-16">
          <h2 class="text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl mb-4">{{ $t('contact_us') }}</h2>
          <p class="text-lg text-gray-500 max-w-2xl mx-auto">{{ $t('ask_details') }}</p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          
          <!-- Contact Form Card -->
          <div class="bg-white rounded-2xl shadow-xl overflow-hidden order-2 lg:order-1">
            <div class="p-8 md:p-10">
              <h3 class="text-2xl font-bold text-gray-900 mb-6">Send us a message</h3>
              <form class="space-y-6" @submit="sendMessage">
                
                <!-- Error Alert -->
                <div v-if="errors.length" class="bg-red-50 border-l-4 border-red-500 p-4 rounded-md animate-pulse">
                   <div class="flex">
                    <div class="flex-shrink-0">
                      <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="ml-3">
                      <h3 class="text-sm font-medium text-red-800">There were errors with your submission</h3>
                      <div class="mt-2 text-sm text-red-700">
                        <ul class="list-disc pl-5 space-y-1">
                          <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <!-- Name -->
                  <div>
                    <label for="name" class="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                    <div class="relative rounded-md shadow-sm">
                        <input 
                            type="text" 
                            class="block w-full px-4 py-3 rounded-lg border-gray-300 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm transition-colors duration-200 ease-in-out" 
                            id="name" 
                            v-model="name" 
                            placeholder="Your Name" 
                            name="name"
                            required 
                        />
                    </div>
                  </div>

                  <!-- Phone -->
                  <div>
                    <label for="phone" class="block text-sm font-semibold text-gray-700 mb-2">{{ $t('your_phone', 'Phone number') }}</label>
                    <div class="relative rounded-md shadow-sm">
                        <input 
                            type="tel" 
                            class="block w-full px-4 py-3 rounded-lg border-gray-300 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm transition-colors duration-200 ease-in-out" 
                            id="phone" 
                            v-model="phone" 
                            placeholder="+32 ..."
                            name="phone" 
                            required 
                        />
                    </div>
                  </div>

                  <!-- Email -->
                  <div class="md:col-span-2">
                    <label for="email" class="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <div class="relative rounded-md shadow-sm">
                        <input 
                            type="email" 
                            class="block w-full px-4 py-3 rounded-lg border-gray-300 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm transition-colors duration-200 ease-in-out" 
                            id="email" 
                            v-model="email" 
                            placeholder="you@company.com" 
                            name="email"
                            required 
                        />
                    </div>
                  </div>

                  <!-- Message -->
                  <div class="md:col-span-2">
                    <label for="message" class="block text-sm font-semibold text-gray-700 mb-2">{{ $t('message', 'Write Your Message') }}</label>
                    <div class="relative rounded-md shadow-sm">
                        <textarea 
                            class="block w-full px-4 py-3 rounded-lg border-gray-300 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm transition-colors duration-200 ease-in-out" 
                            placeholder="How can we help you?" 
                            id="message" 
                            v-model="message"
                            name="message" 
                            rows="5"
                        ></textarea>
                    </div>
                  </div>
                </div>

                <div class="pt-4" v-if="pageReady">
                  <NuxtTurnstile :options="turnstileOptions" :site-key="siteKey" ref="turnstile" v-model="turnStileToken" class="mb-4" />
                  <button 
                    type="submit" 
                    :disabled="!turnStileToken"
                    class="w-full flex justify-center py-3 px-6 border border-transparent rounded-lg shadow-md text-base font-bold text-white bg-[#D73502] hover:bg-[#b02b02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D73502] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.01]"
                  >
                    {{ t('send_message') }}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <!-- Contact Info -->
          <div class="space-y-8 order-1 lg:order-2">
            
            <!-- Office Card -->
             <div class="bg-white rounded-2xl shadow-lg p-8 transform transition-all hover:-translate-y-1 hover:shadow-xl duration-300 border border-gray-100">
                <div class="flex items-center gap-4 mb-6">
                     <div class="p-3 bg-blue-50 rounded-full text-[#D73502]">
                        <i class="fa fa-building text-2xl"></i>
                     </div>
                     <h4 class="font-bold text-xl text-gray-900">{{ $t('our_office') }}</h4>
                </div>
                
                <ul class="space-y-6">
                  <li class="flex items-start">
                    <div class="flex-shrink-0">
                       <div class="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-50 text-indigo-600">
                           <i class="fa fa-map-marker-alt"></i>
                       </div>
                    </div>
                    <div class="ml-4">
                      <p class="text-sm font-medium text-gray-900">{{ $t('address_warehouse') }}</p>
                      <a href="https://goo.gl/maps/dhYhJ4nTXMm" target="_blank" class="mt-1 text-base text-gray-600 hover:text-[#D73502] transition-colors block">
                        Rue du Grand Champs 14, <br>5380 Fernelmont, Belgium
                      </a>
                    </div>
                  </li>
                  <li class="flex items-center">
                    <div class="flex-shrink-0">
                        <div class="flex items-center justify-center w-10 h-10 rounded-full bg-green-50 text-green-600">
                            <i class="fa fa-phone"></i>
                        </div>
                    </div>
                    <div class="ml-4">
                      <p class="text-sm font-medium text-gray-900">{{ $t('phone') }}</p>
                      <a href="tel:+3281830202" class="mt-1 text-base text-gray-600 hover:text-[#D73502] transition-colors">
                        +32 (0) 81 83 02 02
                      </a>
                    </div>
                  </li>
                  <li class="flex items-center">
                     <div class="flex-shrink-0">
                        <div class="flex items-center justify-center w-10 h-10 rounded-full bg-purple-50 text-purple-600">
                            <i class="fa fa-envelope"></i>
                        </div>
                    </div>
                    <div class="ml-4">
                      <p class="text-sm font-medium text-gray-900">{{ $t('email') }}</p>
                      <a href="mailto:infos@cld.be" class="mt-1 text-base text-gray-600 hover:text-[#D73502] transition-colors">
                        infos@cld.be
                      </a>
                    </div>
                  </li>
                </ul>
             </div>

             <!-- Hours Card -->
             <div class="bg-white rounded-2xl shadow-lg p-8 transform transition-all hover:-translate-y-1 hover:shadow-xl duration-300 border border-gray-100">
                 <div class="flex items-center gap-4 mb-6">
                     <div class="p-3 bg-orange-50 rounded-full text-orange-500">
                        <i class="fa fa-clock text-2xl"></i>
                     </div>
                     <h4 class="font-bold text-xl text-gray-900">{{ $t('business_hours.text') }} <span class="text-sm font-normal text-gray-500 ml-2">(GMT+1)</span></h4>
                </div>

                <div class="space-y-4">
                     <div class="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                         <span class="text-gray-600 font-medium">{{ $t('office') }}</span>
                         <span class="text-gray-900 font-bold font-mono">{{ $t('business_hours.office') }}</span>
                     </div>
                     <div class="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                         <span class="text-gray-600 font-medium">{{ $t('warehouse') }}</span>
                         <span class="text-gray-900 font-bold font-mono">{{ $t('business_hours.warehouse') }}</span>
                     </div>
                      <div class="flex justify-between items-center py-3 border-b border-gray-100 last:border-0 bg-red-50 -mx-8 px-8 mt-2">
                         <span class="text-red-600 font-medium">{{ $t('business_hours.closed') }}</span>
                         <!-- <span class="text-red-700 font-bold uppercase tracking-wide text-xs">{{ $t('closed') }}</span> -->
                     </div>
                </div>
             </div>

             <div class="text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
                <p class="text-gray-500 italic">
                  "{{ $t('no_answer_found') }}"
                </p>
             </div>

          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script setup>
import { useI18n } from "vue-i18n";
import { useUserStore } from "../../store/UserStore"

const userStore = useUserStore();
const { t } = useI18n();
const siteKey = useRuntimeConfig().public.turnstileSiteKey
const turnstileOptions = useTurnstileTheme();
const turnStileToken = ref("")
const turnstile = ref()
const contactEmail = computed(() => userStore.contactEmail)
const errors = ref([]);
const name = ref(null);
const email = ref(null);
const phone = ref(null);
const message = ref(null);
const pageReady = ref(false);


const checkForm = (e) => {
  errors.value = []
  if (!name.value) {
    errors.value.push('Name required.')
  }

  if (!email.value) {
    errors.value.push('Email required.')
  } else if (!validEmail(email.value)) {
    errors.value.push('Valid email required.')
  }
  if (!phone.value) {
    errors.value.push('Phone Number required.')
  }
  if (!message.value) {
    errors.value.push('Message required.')
  }
  if (!errors.value.length) return true
  e.preventDefault()
}

const sendMessage = async (e) => {
  e.preventDefault()
  if (!checkForm(e)) {
    return false
  }

  await userStore.sendContactEmailCld({
    name: name.value,
    message: message.value,
    email: email.value,
    phone: phone.value, token: turnStileToken.value
  });

  if (contactEmail.value) {
    useNuxtApp().$showToast({
      msg: t('contact_email_sent'),
      type: "success",
    });
    name.value = "";
    message.value = "";
    email.value = "";
    phone.value = "";
    turnstile.value?.reset();

  }
  else {
    useNuxtApp().$showToast({
      msg: t('save_fail.title', 'Error sending email'),
      type: "error",
    });
  }

}
const validEmail = (email) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email)
}

onNuxtReady(() => {

  setTimeout(() => {
    pageReady.value = true;
  }, 100);
});
</script>
<style scoped>
/* Tailwind classes used - no custom CSS needed */
</style>
